import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2, Send, ShieldAlert, ShieldCheck } from 'lucide-react';
import { authService, type UserProfileDto } from '../../services/authService';
import { twoFactorService } from '../../services/twoFactorService';
import { ChannelBotDesktopLinkPanel } from '../twoFactor/ChannelBotDesktopLinkPanel';
import { openChannelBotDeepLink } from '../../utils/openChannelBotDeepLink';
import { isMobileDevice } from '../../utils/isMobileDevice';
import {
  getIranPhoneNumberFormatError,
  iranPhonesAreEquivalent,
  isStoredE164Phone,
  isValidIranPhoneNumber,
  normalizeIranPhoneNumber,
} from '../../utils/iranPhoneNumber';
import { getApiErrorMessage } from '../../services/api';

const AVAILABILITY_DEBOUNCE_MS = 450;

type AvailabilityState = 'idle' | 'checking' | 'available' | 'taken' | 'current';

interface ProfilePhoneVerificationFieldProps {
  value: string;
  savedPhone?: string | null;
  savedConfirmed?: boolean;
  disabled?: boolean;
  onChange: (value: string) => void;
  onVerified: (profile: UserProfileDto) => void;
}

function phoneErrorFields(error: unknown): {
  message: string;
  errorCode?: string;
  messageKey?: string;
  telegramLinked?: boolean;
  cooldownSeconds?: number;
  developmentCode?: string | null;
} {
  const data =
    error && typeof error === 'object' && 'responseData' in error
      ? ((error as { responseData?: Record<string, unknown> }).responseData ?? {})
      : {};
  return {
    message: getApiErrorMessage(error, ''),
    errorCode: String(data.errorCode ?? data.ErrorCode ?? '') || undefined,
    messageKey: String(data.messageKey ?? data.MessageKey ?? '') || undefined,
    telegramLinked: Boolean(data.telegramLinked ?? data.TelegramLinked),
    cooldownSeconds: Number(data.cooldownSeconds ?? data.CooldownSeconds ?? 0),
    developmentCode: (data.developmentCode ?? data.DevelopmentCode) as string | null | undefined,
  };
}

export const ProfilePhoneVerificationField: React.FC<ProfilePhoneVerificationFieldProps> = ({
  value,
  savedPhone,
  savedConfirmed = false,
  disabled,
  onChange,
  onVerified,
}) => {
  const { t } = useTranslation('profile');
  const { t: tSettings } = useTranslation('settings');
  const isDev = import.meta.env.DEV;

  const [formatError, setFormatError] = useState<string | null>(null);
  const [availability, setAvailability] = useState<AvailabilityState>('idle');
  const [actionError, setActionError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const [devCode, setDevCode] = useState<string | null>(null);
  const [telegramLinked, setTelegramLinked] = useState(false);
  const [telegramBotUsername, setTelegramBotUsername] = useState<string | null>(null);
  const [deepLink, setDeepLink] = useState('');
  const [polling, setPolling] = useState(false);
  const [linkWaitExpired, setLinkWaitExpired] = useState(false);
  const [devChatId, setDevChatId] = useState('');

  const linkTokenRef = useRef('');
  const deepLinkRef = useRef('');
  const pollGenerationRef = useRef(0);
  const availabilityRequestRef = useRef(0);

  const commitStoredFormat = () => {
    if (!value.trim()) return;
    const stored = normalizeIranPhoneNumber(value);
    if (isStoredE164Phone(stored) && stored !== value) {
      onChange(stored);
    }
  };
  const normalizedValue = value.trim() ? normalizeIranPhoneNumber(value) : '';
  const isEmpty = !value.trim();
  const isFormatValid = isValidIranPhoneNumber(value);
  const matchesSaved = iranPhonesAreEquivalent(value, savedPhone ?? '');
  const needsVerification = !isEmpty && isFormatValid && !matchesSaved;
  const savedUnverified = matchesSaved && !!normalizedValue && !savedConfirmed;

  const translateApi = useCallback(
    (messageKey?: string, fallback?: string) => {
      if (messageKey) {
        const translated = t(messageKey);
        if (translated && translated !== messageKey) {
          return translated;
        }
      }
      return fallback || '';
    },
    [t]
  );

  const refreshTelegramStatus = useCallback(async () => {
    try {
      const status = await twoFactorService.getStatus();
      setTelegramLinked(status.telegramLinked);
      setTelegramBotUsername(status.telegramBotUsername ?? null);
      return status.telegramLinked;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    void refreshTelegramStatus();
  }, [refreshTelegramStatus]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  useEffect(() => {
    setFormatError(getIranPhoneNumberFormatError(value) ? t('formEdit.phoneInvalid') : null);
    setCode('');
    setCodeSent(false);
    setDevCode(null);
    setActionError(null);

    if (isEmpty || !isFormatValid) {
      setAvailability('idle');
      return;
    }
    if (matchesSaved) {
      setAvailability('current');
      return;
    }

    const requestId = ++availabilityRequestRef.current;
    setAvailability('checking');
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const result = await authService.checkPhoneAvailability(normalizeIranPhoneNumber(value));
          if (availabilityRequestRef.current !== requestId) return;
          if (!result.isValid) {
            setAvailability('idle');
            setFormatError(translateApi(result.messageKey, result.message));
            return;
          }
          setAvailability(result.isAvailable ? 'available' : 'taken');
          if (!result.isAvailable) {
            setActionError(translateApi(result.messageKey, result.message));
          }
        } catch (error) {
          if (availabilityRequestRef.current !== requestId) return;
          setAvailability('idle');
          setActionError(getApiErrorMessage(error, t('formEdit.phoneSendFailed')));
        }
      })();
    }, AVAILABILITY_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [isEmpty, isFormatValid, matchesSaved, t, translateApi, value]);

  const fetchBotLink = useCallback(async () => {
    if (linkTokenRef.current && deepLinkRef.current) {
      return { linkToken: linkTokenRef.current, deepLink: deepLinkRef.current };
    }
    const link = await twoFactorService.getTelegramLink();
    const raw = link as unknown as Record<string, unknown>;
    const linkToken = String(raw.linkToken ?? raw.LinkToken ?? '');
    const deepLinkValue = String(raw.deepLink ?? raw.DeepLink ?? '');
    linkTokenRef.current = linkToken;
    deepLinkRef.current = deepLinkValue;
    setDeepLink(deepLinkValue);
    return { linkToken, deepLink: deepLinkValue };
  }, []);

  useEffect(() => {
    if (telegramLinked || isMobileDevice() || !needsVerification) {
      return;
    }
    if (linkTokenRef.current) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        await fetchBotLink();
      } catch {
        if (cancelled) return;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchBotLink, needsVerification, telegramLinked]);

  const pollLink = useCallback(
    async (token: string) => {
      const generation = ++pollGenerationRef.current;
      setPolling(true);
      setLinkWaitExpired(false);
      try {
        for (let i = 0; i < 40; i++) {
          if (pollGenerationRef.current !== generation) return;
          try {
            const status = await twoFactorService.getTelegramLinkStatus(token);
            if (status.linked) {
              setTelegramLinked(true);
              setPolling(false);
              return;
            }
          } catch {
            /* ignore transient poll errors */
          }
          await new Promise((r) => setTimeout(r, 3000));
        }
        if (pollGenerationRef.current === generation) {
          setLinkWaitExpired(true);
        }
      } finally {
        if (pollGenerationRef.current === generation) {
          setPolling(false);
        }
      }
    },
    []
  );

  const startTelegramLink = async () => {
    setBusy(true);
    setActionError(null);
    try {
      const link = await fetchBotLink();
      if (isMobileDevice() && link.deepLink) {
        await openChannelBotDeepLink(link.deepLink, 'telegram');
      }
      void pollLink(link.linkToken);
    } catch (error) {
      setActionError(getApiErrorMessage(error, tSettings('security.botLinkStartFailed')));
    } finally {
      setBusy(false);
    }
  };

  const sendCode = async () => {
    if (!isFormatValid || availability === 'taken') return;
    setBusy(true);
    setActionError(null);
    try {
      const linked = telegramLinked || (await refreshTelegramStatus());
      if (!linked) {
        setActionError(t('formEdit.phoneTelegramRequired'));
        return;
      }
      const stored = normalizeIranPhoneNumber(value);
      if (stored && stored !== value) {
        onChange(stored);
      }
      const result = await authService.sendPhoneVerificationCode(stored);
      setCodeSent(true);
      setCooldown(result.cooldownSeconds || 30);
      setDevCode(result.developmentCode ?? null);
    } catch (error) {
      const parsed = phoneErrorFields(error);
      if (parsed.errorCode === 'PHONE_TELEGRAM_NOT_LINKED') {
        setTelegramLinked(false);
      }
      if (parsed.cooldownSeconds > 0) {
        setCooldown(parsed.cooldownSeconds);
        setCodeSent(true);
      }
      if (parsed.developmentCode) {
        setDevCode(parsed.developmentCode);
      }
      setActionError(translateApi(parsed.messageKey, parsed.message) || t('formEdit.phoneSendFailed'));
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (submittedCode = code) => {
    const trimmed = submittedCode.replace(/\D/g, '');
    if (trimmed.length !== 6) return;
    setBusy(true);
    setActionError(null);
    try {
      const result = await authService.verifyPhone({
        phoneNumber: normalizeIranPhoneNumber(value),
        verificationCode: trimmed,
      });
      if (result.profile) {
        onVerified(result.profile);
      }
      setCode('');
      setCodeSent(false);
      setDevCode(null);
    } catch (error) {
      const parsed = phoneErrorFields(error);
      setActionError(translateApi(parsed.messageKey, parsed.message) || t('formEdit.phoneCodeInvalid'));
    } finally {
      setBusy(false);
    }
  };

  const inputClass = `w-full px-4 py-3 border-2 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-4 transition-all duration-200 disabled:bg-gray-50 dark:disabled:bg-gray-800/50 disabled:cursor-not-allowed ${
    formatError || availability === 'taken'
      ? 'border-red-400 dark:border-red-500 focus:border-red-500 dark:focus:border-red-400 focus:ring-red-100 dark:focus:ring-red-900/40'
      : matchesSaved && savedConfirmed
        ? 'border-emerald-300 dark:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-400 focus:ring-emerald-100 dark:focus:ring-emerald-900/40'
        : 'border-gray-200 dark:border-gray-600 focus:border-purple-500 dark:focus:border-purple-400 focus:ring-purple-100 dark:focus:ring-purple-900/40'
  }`;

  return (
    <div className="space-y-3">
      <input
        type="tel"
        value={value}
        autoComplete="tel"
        inputMode="tel"
        onChange={(e) => onChange(e.target.value)}
        onBlur={commitStoredFormat}
        placeholder={t('formEdit.phonePlaceholder')}
        className={inputClass}
        disabled={disabled || busy}
      />

      <div className="flex flex-wrap items-center gap-2">
        {matchesSaved && normalizedValue && savedConfirmed && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
            <ShieldCheck className="h-3.5 w-3.5" />
            {t('formEdit.phoneVerified')}
          </span>
        )}
        {savedUnverified && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
            <ShieldAlert className="h-3.5 w-3.5" />
            {t('formEdit.phoneUnverified')}
          </span>
        )}
        {availability === 'checking' && (
          <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {t('formEdit.phoneCheckingAvailability')}
          </span>
        )}
        {availability === 'available' && (
          <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2.5 py-1 text-[11px] font-semibold text-purple-700 dark:bg-purple-900/40 dark:text-purple-200">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {t('formEdit.phoneAvailable')}
          </span>
        )}
      </div>

      {formatError && <p className="text-xs text-red-600 dark:text-red-400">{formatError}</p>}
      {availability === 'taken' && (
        <p className="text-xs text-red-600 dark:text-red-400">{t('formEdit.phoneTaken')}</p>
      )}
      {isEmpty && !!savedPhone && (
        <p className="text-xs text-amber-700 dark:text-amber-300">{t('formEdit.phoneWillBeRemoved')}</p>
      )}
      {actionError && availability !== 'taken' && (
        <p className="text-xs text-red-600 dark:text-red-400">{actionError}</p>
      )}

      <p className="text-xs text-gray-500 dark:text-gray-400">{t('formEdit.phoneHint')}</p>

      {(needsVerification || savedUnverified) && availability !== 'taken' && isFormatValid && (
        <div className="space-y-3 rounded-xl border border-purple-200/80 bg-purple-50/60 p-3 dark:border-purple-800/60 dark:bg-purple-950/20">
          <p className="text-xs text-purple-900 dark:text-purple-100">{t('formEdit.phoneVerifyHint')}</p>

          {!telegramLinked && (
            <div className="space-y-2">
              <p className="text-xs text-slate-600 dark:text-slate-300">{t('formEdit.phoneTelegramRequired')}</p>
              {!isMobileDevice() && deepLink && (
                <ChannelBotDesktopLinkPanel
                  method="telegram"
                  deepLink={deepLink}
                  polling={polling}
                  linkWaitExpired={linkWaitExpired}
                  showLocalhostHint={isDev}
                  checkDisabled={busy}
                  onCheckLink={() => {
                    if (linkTokenRef.current) {
                      void pollLink(linkTokenRef.current);
                    } else {
                      void startTelegramLink();
                    }
                  }}
                />
              )}
              <button
                type="button"
                disabled={disabled || busy || polling}
                onClick={() => void startTelegramLink()}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {polling ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {polling ? t('formEdit.phoneWaitingTelegram') : t('formEdit.phoneOpenTelegram')}
              </button>
              {telegramBotUsername && (
                <p className="text-[11px] text-slate-500">@{telegramBotUsername}</p>
              )}
              {isDev && (
                <details className="rounded-lg border border-dashed border-amber-400/80 bg-amber-50/70 p-3 dark:border-amber-700/60 dark:bg-amber-950/20">
                  <summary className="cursor-pointer text-xs font-medium text-amber-900 dark:text-amber-100">
                    {tSettings('security.linkWithTelegramIdSummary')}
                  </summary>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={devChatId}
                      onChange={(e) => setDevChatId(e.target.value.replace(/[^\d]/g, ''))}
                      placeholder={tSettings('security.chatIdPlaceholderTelegram')}
                      className="w-full max-w-xs rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
                    />
                    <button
                      type="button"
                      disabled={busy || !devChatId.trim()}
                      onClick={() => {
                        void (async () => {
                          setBusy(true);
                          try {
                            await twoFactorService.linkTelegramChatId(Number(devChatId.trim()));
                            setTelegramLinked(true);
                            setDevChatId('');
                          } catch (error) {
                            setActionError(getApiErrorMessage(error, tSettings('security.botLinkStartFailed')));
                          } finally {
                            setBusy(false);
                          }
                        })();
                      }}
                      className="rounded-lg border border-amber-400 px-3 py-2 text-xs font-semibold text-amber-900 dark:text-amber-100"
                    >
                      {tSettings('security.telegramLinked')}
                    </button>
                  </div>
                </details>
              )}
            </div>
          )}

          {telegramLinked && (
            <div className="space-y-2">
              <p className="text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                {t('formEdit.phoneTelegramLinked')}
              </p>
              <button
                type="button"
                disabled={disabled || busy || cooldown > 0}
                onClick={() => void sendCode()}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {cooldown > 0
                  ? t('formEdit.phoneResendIn', { seconds: cooldown })
                  : codeSent
                    ? t('formEdit.phoneResendCode')
                    : t('formEdit.phoneSendCode')}
              </button>
            </div>
          )}

          {codeSent && (
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300">
                {t('formEdit.phoneCodeLabel')}
              </label>
              <div className="flex flex-wrap gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => {
                    const next = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setCode(next);
                    if (next.length === 6) {
                      void verifyCode(next);
                    }
                  }}
                  placeholder={t('formEdit.phoneCodePlaceholder')}
                  className="w-36 tracking-[0.35em] text-center text-lg font-semibold px-3 py-2.5 border-2 border-purple-300 dark:border-purple-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-4 focus:ring-purple-100 dark:focus:ring-purple-900/40"
                  disabled={disabled || busy}
                />
                <button
                  type="button"
                  disabled={disabled || busy || code.replace(/\D/g, '').length !== 6}
                  onClick={() => void verifyCode()}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-purple-600 px-4 py-2.5 text-sm font-semibold text-purple-700 hover:bg-purple-50 dark:text-purple-200 dark:hover:bg-purple-900/40 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {t('formEdit.phoneVerify')}
                </button>
              </div>
              {devCode && (
                <p className="text-[11px] text-amber-800 dark:text-amber-200">
                  <Trans
                    i18nKey="formEdit.phoneDevelopmentCode"
                    ns="profile"
                    values={{ code: devCode }}
                    components={{ strong: <strong className="tracking-widest" /> }}
                  />
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
